const TIER_CAPABILITIES = {
  manage: new Set(['job_board', 'crm_contacts', 'crm_lists', 'talent_communities', 'analytics']),
  connect: new Set([
    'job_board',
    'crm_contacts',
    'contacts_import',
    'crm_lists',
    'movements',
    'sharing_contacts_lists',
    'talent_communities',
    'campaigns',
    'analytics',
    'invite_allies',
  ]),
  scale: new Set([
    'job_board',
    'crm_contacts',
    'contacts_import',
    'crm_lists',
    'movements',
    'sharing_contacts_lists',
    'talent_communities',
    'campaigns',
    'people_finder',
    'company_research',
    'analytics',
    'invite_allies',
  ]),
};

const LEGACY_WITH_JOB_BOARD = new Set([
  'job_board',
  'crm_contacts',
  'contacts_import',
  'crm_lists',
  'sharing_contacts_lists',
  'analytics',
  'invite_allies',
]);

const LEGACY_WITHOUT_JOB_BOARD = new Set([
  'crm_contacts',
  'contacts_import',
  'crm_lists',
  'sharing_contacts_lists',
  'analytics',
  'invite_allies',
]);

// Capabilities that require the user to have a Findem account (findemUserId).
// Access is denied even if the network tier includes the capability.
// This is a stepping stone toward full user-role-based capability gating.
const FINDEM_USER_CAPABILITIES = new Set(['talent_communities', 'campaigns', 'people_finder', 'company_research']);

/**
 * Derives the tier classification from a collection/network object.
 *
 * @param {object} collection - Network object from Redux store (camelCase fields).
 * @returns {{ type: 'new_tier', tier: string } | { type: 'legacy', hasJobBoard: boolean, isPaidConnect: boolean } | { type: 'non_customer' }}
 */
export function deriveTier(collection) {
  if (!collection) {
    return { type: 'non_customer' };
  }

  if (collection.vcBundleTier) {
    return { type: 'new_tier', tier: collection.vcBundleTier };
  }

  if (collection.connect?.hasAccess || collection.connectPremium || collection.connect?.hasPremium) {
    return {
      type: 'legacy',
      hasJobBoard: Boolean(collection.features?.includes('job_board')),
      isPaidConnect: Boolean(collection.connectPremium || collection.connect?.hasPremium),
    };
  }

  // GetroJobs-only: all GJ customers should have free Connect; treat as GJ+GC (3k contact limit).
  if (collection.features?.includes('job_board')) {
    return {
      type: 'legacy',
      hasJobBoard: true,
      isPaidConnect: false,
    };
  }

  return { type: 'non_customer' };
}

/**
 * Checks whether a collection qualifies as a customer (paying or otherwise active).
 *
 * @param {object} collection - Network object from Redux store.
 * @returns {boolean}
 */
export function isCustomer(collection) {
  if (!collection) return false;

  return Boolean(
    collection.chargebeeCustomerId ||
      collection.vcBundleTier ||
      collection.features?.includes('job_board') ||
      collection.connectPremium ||
      collection.connect?.hasPremium ||
      collection.connect?.hasAccess,
  );
}

/**
 * Checks whether a given tier result has a specific capability.
 * Enterprise is treated as Scale for feature visibility.
 *
 * Capabilities in FINDEM_USER_CAPABILITIES additionally require the user to have
 * a `findemUserId`. When `user` is omitted, only the tier check is performed
 * (backward-compatible for callers without user context).
 *
 * @param {object} tierResult - Output from deriveTier().
 * @param {string} capability - Capability key (e.g. 'contacts_import', 'campaigns').
 * @param {object} [user] - User object from Redux store. Findem-specific capabilities
 *   are denied unless `user.findemUserId` is set. Omitting `user` denies by default.
 * @returns {boolean}
 */
export function hasCapability(tierResult, capability, user) {
  if (!tierResult) return false;

  // Deny Findem capabilities unless the user has a provisioned Findem account.
  if (FINDEM_USER_CAPABILITIES.has(capability) && !user?.findemUserId) {
    return false;
  }

  if (tierResult.type === 'new_tier') {
    const effectiveTier = tierResult.tier === 'enterprise' ? 'scale' : tierResult.tier;
    const caps = TIER_CAPABILITIES[effectiveTier];
    return caps ? caps.has(capability) : false;
  }

  if (tierResult.type === 'legacy') {
    const caps = tierResult.hasJobBoard ? LEGACY_WITH_JOB_BOARD : LEGACY_WITHOUT_JOB_BOARD;
    return caps.has(capability);
  }

  return false;
}

/**
 * Shortcut: whether the tier result grants access to contacts/connect features.
 * New tiers always have access; legacy requires connect.hasAccess; non-customers do not.
 *
 * @param {object} tierResult - Output from deriveTier().
 * @returns {boolean}
 */
export function hasAccessToContacts(tierResult) {
  if (!tierResult) return false;

  if (tierResult.type === 'new_tier') return true;
  if (tierResult.type === 'legacy') return true;

  return false;
}

/**
 * Whether the network is subject to a contacts limit.
 * Only legacy networks with the connect_premium flag have an enforced cap;
 * new-tier networks (connect, scale, enterprise, manage) never do.
 *
 * @param {object} tierResult - Output from deriveTier().
 * @param {object} collection - Network object from Redux store.
 * @returns {boolean}
 */
export function hasContactsLimit(tierResult, collection) {
  if (!tierResult || !collection) return false;
  return tierResult.type === 'legacy' && !collection.features?.includes('connect_premium');
}
